import './Tooltip.css';
import React, { useState, useEffect, useRef } from 'react';
import GlobalProps from './../globalProps';
import classnames from 'classnames';
import { Manager, Reference, Popper } from 'react-popper';
import * as PopperJS from 'popper.js';

export interface Props extends GlobalProps {
  reference: JSX.Element;
  arrow?: boolean;
  position?: PopperJS.Placement;
  elevated?: boolean;
  color?: string;
  gap?: 'none' | 'small' | 'medium' | 'large';
  darkMode?: boolean;
  mode?: 'click' | 'hover';
  rounded?: boolean;
  open?: boolean;
  onChange?: (open: boolean) => void;
  disableRepositioning?: boolean;
  positionFixed?: boolean;
  dontCloseOnParentClick?: boolean;
  closeOnChildrenClick?: boolean;
  small?: boolean;
  padding?: 'none' | 'small' | 'medium';
  fontSize?: 'small' | 'normal';
  disabled?: boolean;
  maxWidth?: boolean;
  fullReferenceWidth?: boolean;
}

export function Tooltip({
  reference,
  children,
  elevated = true,
  arrow = true,
  position = 'top',
  color,
  className,
  style,
  gap = 'small',
  darkMode,
  mode = 'hover',
  rounded = true,
  open: externalOpen,
  onChange,
  disableRepositioning: escapeWithReference = true,
  positionFixed = true,
  dontCloseOnParentClick,
  closeOnChildrenClick,
  small,
  padding = 'small',
  fontSize = 'normal',
  disabled,
  maxWidth,
  fullReferenceWidth = false,
}: Props): JSX.Element {
  const [open, setOpen] = useState(externalOpen);
  const parentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const propStyle = style;

  function getArrowPosition(pos?: string): string {
    return !pos ? 'top' : pos.split('-')[0];
  }

  function getGapPixels(): number {
    let calculatedGap = arrow ? 8 : 0;

    switch (gap) {
      case 'small':
        calculatedGap += 2;
        break;
      case 'medium':
        calculatedGap += 6;
        break;
      case 'large':
        calculatedGap += 10;
        break;
    }

    return calculatedGap;
  }

  function getContentPadding(pos: string): {
    marginLeft?: number;
    marginBottom?: number;
    marginRight?: number;
    marginTop?: number;
  } {
    const gapPixels = getGapPixels();
    switch (getArrowPosition(pos)) {
      case 'right':
        return { marginLeft: gapPixels };
      case 'top':
        return { marginBottom: gapPixels };
      case 'left':
        return { marginRight: gapPixels };
      default:
        return { marginTop: gapPixels };
    }
  }

  function handleOnOpen(): void {
    if (disabled) return;

    if (onChange) {
      onChange(true);
    }
    if (externalOpen === undefined) {
      setOpen(true);
    }
  }
  function handleOnClose(): void {
    if (disabled) return;

    if (onChange) {
      onChange(false);
    }
    if (externalOpen === undefined) {
      setOpen(false);
    }
  }

  function handleClickOutside(event: MouseEvent): void {
    if (contentRef.current?.contains(event.target as Node)) {
      return;
    }
    if (dontCloseOnParentClick && parentRef.current?.contains(event.target as Node)) {
      return;
    }
    handleOnClose();
  }

  function handleOnScroll(event: Event): void {
    if (contentRef.current?.contains(event.target as Node)) {
      return;
    }
    if (open) {
      handleOnClose();
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll, true);

    if (open && mode === 'click') {
      document.addEventListener('mousedown', handleClickOutside, false);
    }
    // remove subscriptions
    return () => {
      window.removeEventListener('scroll', handleOnScroll, true);
    };
  });

  useEffect(() => {
    setOpen(externalOpen);
  }, [externalOpen]);

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <div
            {...{
              ...(mode === 'hover'
                ? {
                    onMouseEnter: () => handleOnOpen(),
                    onMouseLeave: () => handleOnClose(),
                  }
                : {}),
              ...(mode === 'click'
                ? {
                    onClick: () => handleOnOpen(),
                  }
                : {}),
            }}
            className={classnames('tooltip__parent', {
              'tooltip__parent--full-width': fullReferenceWidth,
            })}
            ref={ref}
          >
            <div className="tooltip__reference" ref={parentRef}>
              {reference}
            </div>
          </div>
        )}
      </Reference>
      {open && (
        <Popper
          positionFixed={positionFixed}
          placement={position}
          modifiers={{
            preventOverflow: { escapeWithReference },
          }}
        >
          {({ ref, style, placement, arrowProps }) => (
            <div
              className={classnames('tooltip', { 'tooltip--dark': darkMode, 'tooltip--small': small }, className)}
              ref={ref}
              style={{
                ...style,
                ...propStyle,
                ...getContentPadding(placement),
              }}
              data-placement={placement}
            >
              <div
                ref={contentRef}
                className={classnames(
                  'tooltip__content',
                  {
                    'tooltip__content--elevated': elevated,
                    'tooltip__content--rounded': rounded,
                    'tooltip__content--font-sm': fontSize === 'small',
                    'tooltip__content--max-width': maxWidth,
                  },
                  `tooltip__content--padding-${padding}`
                )}
                style={{ backgroundColor: color }}
                onClick={closeOnChildrenClick ? () => setOpen(false) : undefined}
              >
                {children}
              </div>
              {arrow && (
                <div
                  className={classnames('tooltip__arrow', `tooltip__arrow--${getArrowPosition(placement)}`)}
                  ref={arrowProps.ref}
                  style={{
                    ...arrowProps.style,
                    color,
                  }}
                />
              )}
            </div>
          )}
        </Popper>
      )}
    </Manager>
  );
}
